import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import cogoToast from 'cogo-toast';
import { getRecommendationGrids, reorderRecommendationGrids } from '../../api/recommendations';
import Loader from '../General/Loader';
import { SortableGridRow } from './elements/SortableGridRow';
import './Grids.scss';

export const Grids = () => {
  const [grids, setGrids] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    fetchGrids();
  }, []);

  const fetchGrids = async () => {
    try {
      setIsLoading(true);
      const response = await getRecommendationGrids();
      setGrids(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching recommendation grids:', error);
      cogoToast.error('Error loading recommendation grids');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = grids.findIndex(item => item.id === active.id);
    const newIndex = grids.findIndex(item => item.id === over.id);

    const newGrids = arrayMove(grids, oldIndex, newIndex);
    setGrids(newGrids);

    try {
      // Prepare grids array with updated sortOrder (sequential: 1, 2, 3...)
      const gridsToReorder = newGrids.map((grid, index) => ({
        id: grid.id,
        sortOrder: index + 1
      }));

      await reorderRecommendationGrids(gridsToReorder);
      cogoToast.success('Grid order updated successfully');
    } catch (error) {
      console.error('Error updating grid order:', error);
      cogoToast.error('Error updating grid order');
      // Revert to original order on error
      setGrids(grids);
    }
  };

  if (isLoading) {
    return (
      <div className='grids-outer-container'>
        <div className='loading-container'>
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className='grids-outer-container'>
      <div className='header-section'>
        <h1>Recommendation Grids</h1>
      </div>

      {grids.length === 0 ? (
        <div className='empty-state'>No recommendation grids found</div>
      ) : (
        <div className='grids-table-container'>
          <table className='grids-table'>
            <thead>
              <tr>
                <th className='drag-handle-header'></th>
                <th>Title</th>
                <th>Slug</th>
                <th>Published</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={grids.map(g => g.id)} strategy={verticalListSortingStrategy}>
                  {grids.map(grid => (
                    <SortableGridRow key={grid.id} grid={grid} />
                  ))}
                </SortableContext>
              </DndContext>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
